/**
 * SAB-1035: what to DO with an nfl-add-drop response, decided in one place.
 *
 * ── WHY A SHARED CLASSIFIER ─────────────────────────────────────────────────
 *
 * Two surfaces call nfl-add-drop's `add` action and each decided independently what a
 * refusal meant. They drifted, badly and asymmetrically: the desktop players page grew a
 * bid mode and a single `if (data.error) toast(...)`; the mobile surface grew no bid path
 * at all, so a phone manager could not place a waiver claim by any route.
 *
 * They are not going to stay in step by being asked to. One classifier, both callers, one
 * place to change when a new refusal appears.
 *
 * ── WHY THE SERVER DECIDES, NOT THE CLIENT ──────────────────────────────────
 *
 * "Is this player on waivers?" was reimplemented client-side from a recent-drop lookup
 * bounded by settings.waivers.drop_lock_days. That is correct only while the drop-lock is
 * the ONLY clause that puts a player on waivers.
 *
 * SAB-1023 adds a second clause (a post-draft window) the lookup cannot see. The client
 * would believe a player is a free agent, send `add`, and be refused. So: attempt the add,
 * and let the REFUSAL say what the user needs. A client-side waiver hint is fine for
 * badging a row, but it must never pick the request -- then any future waiver clause
 * composes for free, because nothing here knows the rules.
 *
 * ── WHY `code` AND NOT THE MESSAGE ──────────────────────────────────────────
 *
 * The add/drop overlay used to branch on message substrings, which is why it dead-ended on
 * refusals it could not tell apart. Every branch here keys on the typed `code`.
 * An UNRECOGNISED code is deliberately an error showing the server's own message -- never
 * silently swallowed, and never optimistically read as a success. That is the direction
 * that actually costs a user: a refusal reported as "added" tells a manager they have a
 * player they do not have.
 */

/** Typed refusal codes nfl-add-drop returns today. Unknown codes fall through to 'error'. */
export type AcquireCode =
  | 'PLAYER_ON_WAIVERS'
  | 'NO_VALID_ROSTER_ARRANGEMENT'
  | 'PLAYER_GAME_STARTED'
  | 'POSITION_NOT_ROSTERABLE'
  | 'PLAYER_ALREADY_ROSTERED'
  | 'PLAYER_NO_LONGER_ON_ROSTER'
  | 'DRAFT_NOT_COMPLETE'
  /** SAB-1030: a healthy player cannot be placed on IR via add. */
  | 'NOT_IR_ELIGIBLE';

export interface AcquireResponse {
  error?: string;
  code?: string;
  message?: string;
  /** PLAYER_ON_WAIVERS only: days until the player clears to free agency. */
  clears_in_days?: number;
  [k: string]: unknown;
}

export type AcquireOutcome =
  /** The add succeeded. `message` is the server's description of what happened. */
  | { kind: 'added'; message: string }
  /**
   * There is no arrangement WITHOUT a drop. Open the drop picker -- this is a question the
   * user can answer.
   */
  | { kind: 'needs_drop' }
  /**
   * The player is on waivers. Not addable by any drop the user could pick, but the
   * intent is still achievable: bid for him. `clearsInDays` is the server's, not ours.
   */
  | { kind: 'needs_bid'; clearsInDays: number | null }
  /** Anything else. `message` is the server's, because it is more specific than ours. */
  | { kind: 'error'; message: string; code: string | null };

const FALLBACK_ERROR = 'That move could not be completed.';

/**
 * Classify one nfl-add-drop response.
 *
 * `httpOk` is passed separately because a 409 carries a perfectly good typed body: the
 * response is not "failed", it is "refused, and here is what to do instead". Callers that
 * only looked at res.ok threw that away.
 */
export function classifyAcquireResponse(
  data: AcquireResponse | null | undefined,
  httpOk = true,
): AcquireOutcome {
  // A null/undefined body is a broken read, not a success. Saying "added" here would tell
  // a manager they got a player they did not get.
  if (!data) return { kind: 'error', message: FALLBACK_ERROR, code: null };

  const code = typeof data.code === 'string' ? data.code : null;

  if (code === 'NO_VALID_ROSTER_ARRANGEMENT') return { kind: 'needs_drop' };

  if (code === 'PLAYER_ON_WAIVERS') {
    // `typeof === 'number'`, not truthiness: 0 is a real value (clears today) and `||`
    // would discard it. Same class as drop_lock_days, and the inverse of the `|| 99`
    // defect in SAB-1025.
    const d = typeof data.clears_in_days === 'number' ? data.clears_in_days : null;
    return { kind: 'needs_bid', clearsInDays: d };
  }

  // Errors are checked BEFORE success, and `error` is checked before `httpOk`. A body
  // carrying an error field is a refusal whatever the status line says -- nfl-add-drop
  // returns some refusals as 400/409 and the generic catch as 500, and a caller that
  // trusted the status alone would report a failure as a success.
  if (data.error || !httpOk) {
    return {
      kind: 'error',
      message: typeof data.error === 'string' && data.error ? data.error : FALLBACK_ERROR,
      code,
    };
  }

  return { kind: 'added', message: typeof data.message === 'string' ? data.message : 'Player added' };
}

/**
 * How long the user is told the player sits on waivers.
 *
 * Separate from the classifier so the wording is testable without a response object, and
 * so both surfaces say the same thing. Null means the server did not tell us -- we say so
 * vaguely rather than inventing a number, because a wrong clear time is worse than none.
 */
export function waiverClearsLabel(clearsInDays: number | null): string {
  if (clearsInDays === null) return 'This player is on waivers.';
  if (clearsInDays <= 0) return 'This player is on waivers and clears today.';
  if (clearsInDays === 1) return 'This player is on waivers and clears in 1 day.';
  return `This player is on waivers and clears in ${clearsInDays} days.`;
}
